import { useMemo } from 'react';

import { useServiceAuditReport } from '@commenda/api/agent-platform/service-auditor/useServiceAuditReport';

import { TCommonTaskProp } from '../../types';
import { ServiceAuditRunStatusHelper } from '../helpers/ServiceAuditRunStatusHelper';
import { useServiceAuditStepStatus } from './useServiceAuditStepStatus';

interface IUseServiceAuditCardParams {
  task: TCommonTaskProp['task'];
  context: TCommonTaskProp['context'];
}

/** The newest audit of an auditor step for reviewers, and the neutral status every other role sees. */
export function useServiceAuditCard({
  task,
  context,
}: Readonly<IUseServiceAuditCardParams>) {
  const { role, workflowExecution } = context;
  const stepId = task.workflowDefinitionStepId ?? null;
  const stepExecutions = workflowExecution?.runtime?.stepExecutions;
  const {
    isReviewerView,
    newestAudit,
    auditStatus,
    watchStepId,
    isLoading: isAuditListLoading,
    error: auditListError,
  } = useServiceAuditStepStatus({
    auditorStepId: stepId,
    role,
    workflowExecution,
  });

  // Disabled for every other role, so the reviewer-only auditor endpoints are never called for them.
  const { report, error: reportError } = useServiceAuditReport({
    runId: newestAudit?.runId,
    enabled: isReviewerView,
  });

  const cardState = ServiceAuditRunStatusHelper.getCardState({
    hasAudits: newestAudit !== null,
    isAuditListLoading,
    auditListError,
    report,
    reportError,
  });
  const runStageRows = useMemo(
    () => ServiceAuditRunStatusHelper.getRunStageRows(report?.frameSteps ?? []),
    [report?.frameSteps]
  );
  const clientStatusLabel = useMemo(
    () =>
      ServiceAuditRunStatusHelper.getClientStatusLabel({
        auditorStepStatus: ServiceAuditRunStatusHelper.getStepExecutionStatus(
          stepExecutions,
          stepId
        ),
        watchStep: watchStepId
          ? {
              status: ServiceAuditRunStatusHelper.getStepExecutionStatus(
                stepExecutions,
                watchStepId
              ),
            }
          : null,
      }),
    [stepExecutions, stepId, watchStepId]
  );

  return {
    // A 404 on the report (auditor flag off) also gets the neutral view.
    isAuditDetailShown:
      isReviewerView &&
      !ServiceAuditRunStatusHelper.isNeutralViewError(reportError),
    cardState,
    report,
    runStageRows,
    clientStatusLabel,
    auditStatus: report?.displayStatus ?? auditStatus,
  };
}
